import { z } from 'zod';
import { MangelSchwere, MangelStatus } from '../enums.js';
/**
 * Freimeldung per QR-Code — der einzige Weg in die Anwendung ohne Anmeldung.
 *
 * Der Ablauf aus der Praxis: Der Betrieb klebt einen QR-Code auf das Gerät.
 * Der Hausmeister sieht einen Mangel behoben, scannt den Code, tippt „habe ich
 * erledigt" — und beim Betreuer erscheint das, ohne dass jemand telefoniert.
 *
 * Warum ohne Konto: „Was viele Kunden abschreckt, ist, wenn die noch irgendwie
 * einen Login kriegen sollen." Ein Zugang, den niemand benutzt, ist kein
 * Zugang.
 *
 * ---
 *
 * SICHERHEITSVERTRAG. Der Token ist das einzige Geheimnis, und er klebt
 * sichtbar an einer Wand. Daraus folgen vier Grenzen, die im Schema und in der
 * Route gemeinsam durchgesetzt werden:
 *
 * 1. Der Token gibt Zugriff auf GENAU EINE Anlage — oder, beim Code am
 *    Begehungsbericht, auf GENAU EINEN Mangel. Nie auf das Objekt, nie auf
 *    den Kunden, nie auf eine Liste.
 * 2. Die Antwort enthält KEINE Kundendaten: kein Kundenname, keine Anschrift,
 *    keine Ansprechpartner, keine Preise. Nur das Gerät und seine offenen
 *    Mängel.
 * 3. Der Kanal kann nur MELDEN, nicht abschließen. Eine Freimeldung setzt den
 *    Status auf `freigemeldet` — behoben wird ein Mangel erst durch die
 *    Nachschau eines Angemeldeten.
 * 4. Er kann nichts anlegen, ändern oder löschen. Kein neuer Mangel, keine
 *    Textänderung an bestehenden.
 *
 * Ein abgelaufener oder widerrufener Token verhält sich wie ein unbekannter —
 * dieselbe Antwort, damit sich gültige Tokens nicht erraten lassen.
 */
/** Öffentlicher Token. Lang genug, dass Raten sinnlos ist; URL-tauglich. */
export const assetTagToken = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{32,64}$/, { message: 'Ungültiger Code' });
export const assetTag = z.object({
    id: z.number().int().positive(),
    anlageId: z.number().int().positive(),
    token: z.string(),
    aktiv: z.boolean(),
    erstelltAm: z.string().datetime(),
    widerrufenAm: z.string().datetime().nullable().optional(),
});
/** Ein Mangel, wie ihn der Scanner sieht — ohne interne Notizen, ohne Fotos. */
const oeffentlicherMangel = z.object({
    id: z.number().int().positive(),
    titel: z.string(),
    beschreibung: z.string().nullable(),
    schwere: MangelSchwere,
    status: MangelStatus,
    festgestelltAm: z.string().datetime(),
    fristBis: z.string().datetime().nullable(),
    /** Schon gemeldet? Dann bleibt der Knopf aus, damit nicht doppelt gemeldet wird. */
    bereitsFreigemeldet: z.boolean(),
});
/**
 * Was der Scanner zu sehen bekommt — Etikett am Gerät.
 *
 * Bewusst karg. `objektBezeichnung` ist der einzige Ortsbezug und dient der
 * Selbstkontrolle („bin ich am richtigen Gerät?") — der Kundenname steht
 * NICHT drin.
 */
export const freimeldungAnsichtAnlage = z.object({
    art: z.literal('anlage'),
    anlageBezeichnung: z.string(),
    anlagenTypLabel: z.string(),
    standortImObjekt: z.string().nullable(),
    objektBezeichnung: z.string(),
    offeneMaengel: z.array(oeffentlicherMangel),
});
/**
 * Was der Scanner zu sehen bekommt — Code am Begehungsbericht.
 *
 * Genau ein Mangel, und nur solange er offen ist. Ein behobener oder
 * hinfälliger Mangel wird NICHT mehr ausgeliefert (`mangel: null`, Anlage und
 * Ort ebenfalls leer) — dieselbe „nichts offen"-Form wie beim Etikett am
 * Gerät. Sonst gäbe ein alter Bericht Titel und Beschreibung dauerhaft preis.
 */
export const freimeldungAnsichtMangel = z.object({
    art: z.literal('mangel'),
    objektBezeichnung: z.string(),
    anlageBezeichnung: z.string().nullable(),
    ortImObjekt: z.string().nullable(),
    mangel: oeffentlicherMangel.nullable(),
});
/** Beide Formen, unterschieden über `art`. */
export const freimeldungAnsicht = z.discriminatedUnion('art', [
    freimeldungAnsichtAnlage,
    freimeldungAnsichtMangel,
]);
/**
 * Die Meldung selbst.
 *
 * `melderName` ist Pflicht: Eine anonyme Freimeldung ist als Nachweis
 * wertlos — im Zweifel muss nachvollziehbar sein, wer was behauptet hat.
 */
export const freimeldungRequest = z.object({
    mangelId: z.number().int().positive(),
    melderName: z.string().trim().min(2).max(120),
    text: z.string().trim().max(2000).optional(),
});
/**
 * Antwort nach der Meldung.
 *
 * Sagt ausdrücklich, dass damit noch nichts abgeschlossen ist — sonst geht der
 * Melder davon aus, die Sache sei erledigt, und die Nachschau kommt
 * überraschend.
 */
export const freimeldungAntwort = z.object({
    angenommen: z.boolean(),
    hinweis: z.string(),
});
export const FREIMELDUNG_HINWEIS = 'Ihre Meldung ist eingegangen. Der Mangel gilt erst als behoben, wenn wir ihn bei der nächsten Kontrolle bestätigt haben.';
